import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Typography, Grid, Box, Button, Pagination, Tabs, Tab, Chip, useMediaQuery, Theme, TextField, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import { getAnimeByStatus, getAnimeByStatusForUser, getProfileAnimeStatuses, getPublicAnimeStatuses } from '../../services/animeApi';
import AnimeCard from '../AnimeCard';
import { UserProfile } from '../../types/UserProfile';
import { Anime } from '../../types/Anime';
import LoadingPlaceholder from '../LoadingPlaceholder';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

const ANIME_PER_PAGE = 30;

interface AnimeLibraryTabProps {
  profile: UserProfile | null;
  isPublic: boolean;
  animeCounts: number[];
  mainTabValue: string;
}

const AnimeLibraryTab: React.FC<AnimeLibraryTabProps> = ({
  profile,
  isPublic,
  animeCounts,
  mainTabValue,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const queryParams = new URLSearchParams(location.search);
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const initialStatus = parseInt(queryParams.get('status') || '1', 10);
  const initialPage = parseInt(queryParams.get('page') || '1', 10);
  const initialSearchQuery = queryParams.get('query') || '';
  const initialOrder = queryParams.get('order') || 'default';

  const [tabValue, setTabValue] = useState(initialStatus);
  const [page, setPage] = useState(initialPage);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [displayQuery, setDisplayQuery] = useState(initialSearchQuery);
  const [order, setOrder] = useState(initialOrder);

  const scrollPositionRef = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // URLを更新する関数
  const updateUrl = useCallback((status: number, pg: number, query: string, ord: string) => {
    const params = new URLSearchParams({
      status: status.toString(),
      page: pg.toString(),
      query,
      order: ord,
      tab: mainTabValue,
      media: 'anime',
    });

    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
  }, [isPublic, username, mainTabValue, navigate]);

  // React Queryでアニメ一覧を取得
  const stableUsername = isPublic ? username : 'me';
  const {
    data: animeData,
    isLoading,
  } = useQuery({
    queryKey: ['profileAnime', stableUsername, tabValue, page, searchQuery, order],
    queryFn: async () => {
      if (isPublic) {
        return getAnimeByStatusForUser(username!, tabValue, page, searchQuery, order);
      } else {
        return getAnimeByStatus(tabValue, page, searchQuery, order);
      }
    },
    enabled: mainTabValue === 'library',
    staleTime: isPublic ? 30000 : 2000,
  });

  const animeList: Anime[] = animeData?.data.results || [];
  const totalAnime = animeData?.data.count || 0;
  const animeTotalPages = Math.ceil(totalAnime / ANIME_PER_PAGE);
  const loadingAnime = isLoading && !animeData;

  // ステータス取得用のmalIds
  const malIds = useMemo(() => animeList.map(a => a.mal_id), [animeList]);

  // React Queryでアニメステータスを取得
  const { data: animeStatusesData } = useQuery({
    queryKey: ['profileAnimeStatuses', stableUsername, malIds],
    queryFn: async () => {
      if (isPublic) {
        return getPublicAnimeStatuses(username!, malIds);
      } else {
        return getProfileAnimeStatuses(malIds);
      }
    },
    enabled: malIds.length > 0,
    staleTime: isPublic ? 30000 : 2000,
  });

  const animeStatuses = animeStatusesData || {};

  // デバウンスされた検索
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setSearchQuery(query);
      setPage(1);
      updateUrl(tabValue, 1, query, order);
    }, 1000),
    [tabValue, order, updateUrl]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setDisplayQuery(query);
    debouncedSearch(query);
  };

  const handleOrderChange = (event: SelectChangeEvent) => {
    const newOrder = event.target.value;
    setOrder(newOrder);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, newOrder);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const currentScroll = scrollPositionRef.current;
    setTabValue(newValue);
    setPage(1);
    updateUrl(newValue, 1, searchQuery, order);
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });
    const target = event.currentTarget as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 0);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    updateUrl(tabValue, value, searchQuery, order);
  };

  const handleClearFilter = () => {
    setSearchQuery('');
    setDisplayQuery('');
    setOrder('default');
    setPage(1);
    updateUrl(tabValue, 1, '', 'default');
  };

  // URLパラメータ変更の監視
  useEffect(() => {
    const newStatus = parseInt(queryParams.get('status') || '1', 10);
    if (newStatus !== tabValue) {
      setTabValue(newStatus);
      setPage(1);
    }
  }, [location.search]);

  const isFilterActive = displayQuery !== '' || order !== 'default';

  const TabLabel: React.FC<{ label: string; count: number; isSelected: boolean }> = ({ label, count, isSelected }) => (
    <Box sx={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', alignItems: 'center' }}>
      <Typography sx={{ mr: isSmallScreen ? 0 : 1, fontSize: isSmallScreen ? '0.75rem' : '0.8rem' }}>{label}</Typography>
      {isSelected ? (
        <Chip
          label={count}
          size="small"
          sx={{
            backgroundColor: 'primary.main',
            color: 'black',
            fontSize: isSmallScreen ? '0.625rem' : '0.75rem',
            height: isSmallScreen ? '16px' : '18px',
          }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.625rem' : '0.75rem' }}>
          /{count}
        </Typography>
      )}
    </Box>
  );

  // ソートオプション
  const orderDisplayText: { [key: string]: string } = {
    'default': 'デフォルト',
    'rating': '評価 (低い順)',
    'rating_desc': '評価 (高い順)',
    'start_date': '視聴開始日 (古い順)',
    'start_date_desc': '視聴開始日 (新しい順)',
    'added_date': '追加日 (古い順)',
    'added_date_desc': '追加日 (新しい順)',
    'year_desc': '放送年 (新しい順)',
    'year_asc': '放送年 (古い順)',
  };

  const orderDisplayTextShort: { [key: string]: string } = {
    'default': 'Sort',
    'rating': '評価↑',
    'rating_desc': '評価↓',
    'start_date': '開始日↑',
    'start_date_desc': '開始日↓',
    'added_date': '追加日↑',
    'added_date_desc': '追加日↓',
    'year_desc': '放送年↓',
    'year_asc': '放送年↑',
  };

  return (
    <>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            maxWidth: '100%',
            '& .MuiTab-root': {
              minWidth: isSmallScreen ? 'auto' : 'inherit',
              padding: isSmallScreen ? '6px 8px' : '6px 16px',
              minHeight: '40px',
              borderRadius: '8px',
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }
            }
          }}
        >
          <Tab label={<TabLabel label="視聴済" count={animeCounts[0]} isSelected={tabValue === 1} />} value={1} />
          <Tab label={<TabLabel label="視聴中" count={animeCounts[1]} isSelected={tabValue === 2} />} value={2} />
          <Tab label={<TabLabel label="中断" count={animeCounts[2]} isSelected={tabValue === 3} />} value={3} />
          <Tab label={<TabLabel label="視聴中止" count={animeCounts[3]} isSelected={tabValue === 4} />} value={4} />
          <Tab label={<TabLabel label={isSmallScreen ? "ウォッチ" : "ウォッチリスト"} count={animeCounts[4]} isSelected={tabValue === 5} />} value={5} />
        </Tabs>
      </Box>

      {/* フィルター */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3, mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            placeholder="Filter..."
            variant="standard"
            size="small"
            value={displayQuery}
            onChange={handleSearchChange}
            sx={{
              flexGrow: 1,
              minWidth: 60,
              '& .MuiInputBase-input::placeholder': {
                opacity: 0.5
              }
            }}
          />
          <Select
            value={order}
            onChange={handleOrderChange}
            displayEmpty
            size="small"
            sx={{
              minWidth: 80,
              height: { xs: '28px', sm: '32px' },
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              borderRadius: '4px',
              '& .MuiSelect-select': {
                paddingY: 0,
                paddingX: 1,
                paddingRight: { xs: '0px !important', sm: '32px !important' },
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                fontSize: { xs: '0.6rem', sm: '0.75rem' },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
              '& .MuiSelect-icon': {
                display: { xs: 'none', sm: 'block' }
              }
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <SortIcon sx={{ mr: 0.5, fontSize: { xs: '0.8rem', sm: '1rem' }, flexShrink: 0 }} />
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
                  {orderDisplayText[selected] || '並び替え'}
                </Box>
                <Box component="span" sx={{ display: { xs: 'block', sm: 'none' } }}>
                  {orderDisplayTextShort[selected] || '並び替え'}
                </Box>
              </Box>
            )}
          >
            <MenuItem value="default" dense>デフォルト</MenuItem>
            <MenuItem value="rating_desc" dense>評価 (高い順)</MenuItem>
            <MenuItem value="rating" dense>評価 (低い順)</MenuItem>
            <MenuItem value="start_date_desc" dense>視聴開始日 (新しい順)</MenuItem>
            <MenuItem value="start_date" dense>視聴開始日 (古い順)</MenuItem>
            <MenuItem value="added_date_desc" dense>追加日 (新しい順)</MenuItem>
            <MenuItem value="added_date" dense>追加日 (古い順)</MenuItem>
            <MenuItem value="year_desc" dense>放送年 (新しい順)</MenuItem>
            <MenuItem value="year_asc" dense>放送年 (古い順)</MenuItem>
          </Select>
        </Box>
        {isFilterActive && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {totalAnime}件
            </Typography>
            <Button
              size="small"
              onClick={handleClearFilter}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{ textTransform: 'none', p: '2px 8px' }}
            >
              Clear
            </Button>
          </Box>
        )}
      </Box>

      {loadingAnime ? (
        <LoadingPlaceholder type="card" count={ANIME_PER_PAGE} />
      ) : (
        <>
          {animeList.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              mt: 2
            }}>
              <Typography variant="h6" gutterBottom>
                まだアニメが登録されていません
              </Typography>
              {!isPublic && (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    アニメを検索して、ライブラリに追加しましょう
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/search?mode=anime')}
                  >
                    アニメを探す
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {animeList.map((anime) => (
                <Grid item xs={4} sm={3} md={2} key={anime.mal_id}>
                  <AnimeCard
                    anime={anime}
                    mode={isPublic ? 'public' : 'library'}
                    initialStatus={animeStatuses[anime.mal_id]?.status}
                    initialScore={animeStatuses[anime.mal_id]?.score}
                    initialStartDate={animeStatuses[anime.mal_id]?.start_date}
                    initialSubStatus={animeStatuses[anime.mal_id]?.sub_status}
                    initialScoreTags={animeStatuses[anime.mal_id]?.score_tags}
                    initialEpisodesWatched={animeStatuses[anime.mal_id]?.episodes_watched}
                    initialWatchMedium={animeStatuses[anime.mal_id]?.watch_medium}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          {animeTotalPages > 1 && (
            <Pagination
              count={animeTotalPages}
              page={page}
              onChange={handlePageChange}
              sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
            />
          )}
        </>
      )}
    </>
  );
};

export default AnimeLibraryTab;
